/**
 * useBreakpoint Hook
 *
 * React hook for responsive design based on window size.
 * Provides current breakpoint and utility functions for conditional rendering.
 *
 * @example
 * const { breakpoint, isMobile, isDesktop, isAbove, isBelow } = useBreakpoint();
 *
 * if (isMobile) {
 *   return <MobileNav />;
 * }
 *
 * if (isAbove('lg')) {
 *   return <DesktopSidebar />;
 * }
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  getCurrentBreakpoint,
  isAbove as checkIsAbove,
  isBelow as checkIsBelow,
  type BreakpointKey,
} from '@/styles/breakpoints';

interface UseBreakpointReturn {
  /** Current breakpoint key (xs, sm, md, lg, xl, 2xl, 3xl) */
  breakpoint: BreakpointKey;

  /** Current window width in pixels */
  width: number;

  /** True if viewport is below tablet (< 768px) */
  isMobile: boolean;

  /** True if viewport is tablet only (768px - 1023px) */
  isTablet: boolean;

  /** True if viewport is tablet or above (>= 768px) */
  isTabletUp: boolean;

  /** True if viewport is desktop or above (>= 1024px) */
  isDesktop: boolean;

  /** True if viewport is large desktop or above (>= 1280px) */
  isLargeDesktop: boolean;

  /** Check if current width is at or above a breakpoint */
  isAbove: (breakpoint: BreakpointKey) => boolean;

  /** Check if current width is below a breakpoint */
  isBelow: (breakpoint: BreakpointKey) => boolean;

  /** Check if current width matches exactly one breakpoint */
  isExact: (breakpoint: BreakpointKey) => boolean;
}

// Debounce delay for resize events (ms)
const RESIZE_DEBOUNCE_MS = 100;

/**
 * Get initial window width (SSR-safe)
 */
function getWindowWidth(): number {
  if (typeof window === 'undefined') {
    return 1024; // Default to desktop for SSR
  }
  return window.innerWidth;
}

/**
 * Hook for responsive breakpoint detection
 */
export function useBreakpoint(): UseBreakpointReturn {
  const [width, setWidth] = useState(getWindowWidth);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    function handleResize() {
      // Debounce resize events
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setWidth(window.innerWidth);
      }, RESIZE_DEBOUNCE_MS);
    }

    // Set initial width
    setWidth(window.innerWidth);

    // Listen for resize events
    window.addEventListener('resize', handleResize, { passive: true });

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Memoize breakpoint calculations
  const breakpoint = useMemo(() => getCurrentBreakpoint(width), [width]);

  const isMobile = useMemo(() => checkIsBelow(width, 'lg'), [width]);
  const isTablet = useMemo(
    () => checkIsAbove(width, 'lg') && checkIsBelow(width, 'xl'),
    [width]
  );
  const isTabletUp = useMemo(() => checkIsAbove(width, 'lg'), [width]);
  const isDesktop = useMemo(() => checkIsAbove(width, 'xl'), [width]);
  const isLargeDesktop = useMemo(() => checkIsAbove(width, '2xl'), [width]);

  const isAbove = useCallback(
    (bp: BreakpointKey) => checkIsAbove(width, bp),
    [width]
  );

  const isBelow = useCallback(
    (bp: BreakpointKey) => checkIsBelow(width, bp),
    [width]
  );

  const isExact = useCallback(
    (bp: BreakpointKey) => getCurrentBreakpoint(width) === bp,
    [width]
  );

  return {
    breakpoint,
    width,
    isMobile,
    isTablet,
    isTabletUp,
    isDesktop,
    isLargeDesktop,
    isAbove,
    isBelow,
    isExact,
  };
}

/**
 * Hook that returns true if the media query matches
 *
 * @example
 * const isLargeScreen = useMediaQuery('(min-width: 1024px)');
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia(query);

    function handleChange(event: MediaQueryListEvent) {
      setMatches(event.matches);
    }

    // Set initial value
    setMatches(mediaQuery.matches);

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    // Legacy browsers (Safari < 14)
    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, [query]);

  return matches;
}

/**
 * Hook for window dimensions
 *
 * @example
 * const { width, height } = useWindowSize();
 */
export function useWindowSize(): { width: number; height: number } {
  const [size, setSize] = useState({
    width: getWindowWidth(),
    height: typeof window !== 'undefined' ? window.innerHeight : 768,
  });

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    function handleResize() {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setSize({
          width: window.innerWidth,
          height: window.innerHeight,
        });
      }, RESIZE_DEBOUNCE_MS);
    }

    // Set initial size
    setSize({
      width: window.innerWidth,
      height: window.innerHeight,
    });

    window.addEventListener('resize', handleResize, { passive: true });

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return size;
}

export default useBreakpoint;
